/**
 * orgKit — small, dependency-free UI primitives shared by the M03 Organization
 * screens. They encode the exact inline-style look already used by the existing
 * read-only m03 list screens (GitHub-primer palette) so the new write screens
 * stay visually consistent without duplicating markup on all ~15 screens.
 *
 * Nothing here is a heavyweight abstraction: each primitive is a thin wrapper
 * around plain HTML elements with the established styles + aria-labels (so the
 * existing test-query style — getByLabelText / getByRole — keeps working).
 */
import { type ReactNode, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DateInput } from '../../../../shared/components/index.js';

// The values below are the Company GST Configuration design. They are inline
// styles, which beat any stylesheet, so tokens/screen-theme.css cannot reach
// these screens — the look has to be carried here instead.
const ink = 'var(--color-text-primary)';
const muted = 'var(--color-text-muted)';
const line = 'var(--color-border-default)';
const accent = 'var(--color-brand-primary)';

export const sx = {
  main: { maxWidth: 1440, padding: 24, color: ink, fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif' } as CSSProperties,
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: `1px solid ${line}`, paddingBottom: 18, marginBottom: 18, gap: 12, flexWrap: 'wrap' } as CSSProperties,
  primaryBtn: { padding: '7px 16px', borderRadius: 4, background: accent, color: 'var(--color-text-inverse)', textDecoration: 'none', border: `1px solid ${accent}`, cursor: 'pointer', fontSize: 14, fontWeight: 700 } as CSSProperties,
  secondaryBtn: { padding: '7px 16px', borderRadius: 4, background: 'var(--color-bg-surface)', color: ink, textDecoration: 'none', border: '1px solid var(--color-border-default)', cursor: 'pointer', fontSize: 14, fontWeight: 600 } as CSSProperties,
  dangerBtn: { padding: '7px 16px', borderRadius: 4, background: 'var(--color-bg-surface)', color: 'var(--color-danger)', border: '1px solid var(--color-danger)', cursor: 'pointer', fontSize: 14, fontWeight: 600 } as CSSProperties,
  search: { width: '100%', maxWidth: 420, minHeight: 38, padding: '7px 9px', marginBottom: 16, border: '1px solid var(--color-border-default)', borderRadius: 4, boxSizing: 'border-box' } as CSSProperties,
  error: { borderLeft: '4px solid var(--color-danger)', background: 'var(--color-danger-bg)', color: 'var(--color-danger)', padding: '10px 12px', margin: '14px 0' } as CSSProperties,
  ok: { borderLeft: `4px solid ${accent}`, background: 'var(--color-success-bg)', padding: '10px 12px', margin: '14px 0' } as CSSProperties,
  table: { width: '100%', border: `1px solid ${line}`, borderCollapse: 'collapse', fontSize: 13, marginTop: 20 } as CSSProperties,
  theadTr: { borderBottom: `1px solid ${line}` } as CSSProperties,
  th: { textAlign: 'left', padding: '10px 12px', background: 'var(--color-bg-muted)', color: 'var(--color-text-secondary)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' } as CSSProperties,
  thRight: { textAlign: 'right', padding: '10px 12px', background: 'var(--color-bg-muted)', color: 'var(--color-text-secondary)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' } as CSSProperties,
  tbodyTr: { borderBottom: `1px solid ${line}` } as CSSProperties,
  td: { padding: '10px 12px', verticalAlign: 'top' } as CSSProperties,
  tdRight: { padding: '10px 12px', textAlign: 'right', verticalAlign: 'top' } as CSSProperties,
  link: { color: accent, fontWeight: 600 } as CSSProperties,
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: muted, margin: '14px 0 6px' } as CSSProperties,
  input: { width: '100%', maxWidth: 420, minHeight: 38, padding: '7px 9px', border: '1px solid var(--color-border-default)', borderRadius: 4, background: 'var(--color-bg-surface)', boxSizing: 'border-box' } as CSSProperties,
  actionBar: { display: 'flex', gap: 8, flexWrap: 'wrap', margin: '16px 0' } as CSSProperties,
  card: { border: `1px solid ${line}`, borderRadius: 4, padding: 18, marginBottom: 16, background: 'var(--color-bg-muted)', maxWidth: 720 } as CSSProperties,
};

/** Map a readonly string[] (enum values) to <option> value/label pairs. */
export function toOptions(values: ReadonlyArray<string>, labeler?: (v: string) => string): ReadonlyArray<{ value: string; label: string }> {
  return values.map((v) => ({ value: v, label: labeler ? labeler(v) : v }));
}

/** Today's date as an ISO `yyyy-mm-dd` string, for effective-date defaults. */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function StatusBadge({ status }: { status: string }): ReactNode {
  const ok = status === 'ACTIVE' || status === 'APPROVED' || status === 'COMPLETED';
  return (
    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, background: ok ? 'var(--color-success-bg)' : 'var(--color-danger-bg)' }}>
      {status}
    </span>
  );
}

/** List-page chrome: header (+ optional New link), optional search, error/loading/empty text, then children (usually the table). */
export function ListShell(props: {
  label: string;
  title: string;
  newTo?: string;
  newLabel?: string;
  search?: { value: string; onChange: (v: string) => void; placeholder?: string };
  error?: string | null;
  loading?: boolean;
  empty?: boolean;
  emptyText?: string;
  children?: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <main aria-label={props.label} style={sx.main}>
      <div style={sx.headerRow}>
        <h1>{props.title}</h1>
        {props.newTo ? (
          <Link to={props.newTo} style={sx.primaryBtn}>{props.newLabel ?? t('org.kit.newBtn')}</Link>
        ) : null}
      </div>
      {props.search ? (
        <input
          aria-label={`${props.label}-search`}
          value={props.search.value}
          onChange={(e) => props.search!.onChange(e.target.value)}
          placeholder={props.search.placeholder ?? t('org.kit.searchPlaceholder')}
          style={sx.search}
        />
      ) : null}
      {props.error ? <p role="alert" style={sx.error}>{props.error}</p> : null}
      {props.loading ? <p>{t('common.loading')}</p> : null}
      {!props.loading && props.empty ? <p>{props.emptyText ?? t('org.kit.noRecords')}</p> : null}
      {props.children}
    </main>
  );
}

/** Form-page chrome: title, error alert, and a <form> that calls onSubmit. */
export function FormShell(props: {
  label: string;
  title: string;
  error?: string | null;
  notice?: string | null;
  onSubmit: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <main aria-label={props.label} style={sx.main}>
      <h1>{props.title}</h1>
      {props.error ? <p role="alert" style={sx.error}>{props.error}</p> : null}
      {props.notice ? <p style={sx.ok}>{props.notice}</p> : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit();
        }}
      >
        {props.children}
      </form>
    </main>
  );
}

export function Field(props: {
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
}): ReactNode {
  return (
    <div>
      <label htmlFor={props.name} style={sx.label}>
        {props.label}{props.required ? ' *' : ''}
      </label>
      {props.type === 'date' || props.type === 'datetime-local' ? <DateInput
        id={props.name}
        aria-label={props.name}
        type={props.type}
        value={props.value}
        onValueChange={props.onChange}
        required={props.required}
        style={sx.input}
      /> : <input
        id={props.name}
        aria-label={props.name}
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        style={sx.input}
      />}
    </div>
  );
}

export function SelectField(props: {
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  required?: boolean;
  placeholder?: string;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div>
      <label htmlFor={props.name} style={sx.label}>
        {props.label}{props.required ? ' *' : ''}
      </label>
      <select
        id={props.name}
        aria-label={props.name}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        style={sx.input}
      >
        <option value="">{props.placeholder ?? t('org.kit.selectPlaceholder')}</option>
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

export function CheckboxField(props: {
  label: string;
  name: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): ReactNode {
  return (
    <div style={{ margin: '12px 0 4px' }}>
      <label htmlFor={props.name} style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        <input
          id={props.name}
          aria-label={props.name}
          type="checkbox"
          checked={props.checked}
          onChange={(e) => props.onChange(e.target.checked)}
          style={{ marginRight: 8 }}
        />
        {props.label}
      </label>
    </div>
  );
}

export function SaveBar(props: {
  busy?: boolean;
  saveLabel?: string;
  cancelTo?: string;
  onCancel?: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <div style={sx.actionBar}>
      <Button type="submit" variant="primary" disabled={props.busy} aria-label="save">
        {props.busy ? t('org.kit.saving') : props.saveLabel ?? t('common.save')}
      </Button>
      <Button
        type="button"
        variant="secondary"
        disabled={props.busy}
        aria-label="cancel"
        onClick={() => (props.onCancel ? props.onCancel() : props.cancelTo ? navigate(props.cancelTo) : navigate(-1))}
      >
        {t('common.cancel')}
      </Button>
    </div>
  );
}
